/* eslint-disable no-nested-ternary */
import { useDevices, useVideoContext } from '@brands/hooks';
import { SELECTED_AUDIO_INPUT_KEY } from '@brands/hooks/video-hooks/useLocalTracks'; // ✅ change if your key differs
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { TbMicrophone, TbMicrophoneOff } from 'react-icons/tb';

import { useLocalAudioToggle } from '../../../hooks';

interface ToggleAudioButtonProps {
  disabled?: boolean;
  className?: string;
  onAfterSelectDevice?: (deviceId: string) => Promise<void> | void;
  layout?: 'mini' | 'full';
}

const ToggleAudioButton: FC<ToggleAudioButtonProps> = ({
  disabled,
  className,
  onAfterSelectDevice,
  layout,
}): JSX.Element => {
  const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();

  const { audioInputDevices } = useDevices();

  const videoCtx = useVideoContext();
  const { replaceAudioTrack } = useVideoContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const [selectedMicId, setSelectedMicId] = useState<string>('');

  useEffect(() => {
    if (!audioInputDevices || audioInputDevices.length === 0) return;

    const stored = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);
    const initial = stored || audioInputDevices[0].deviceId;

    setSelectedMicId(initial);

    if (stored && !audioInputDevices.some((d) => d.deviceId === stored)) {
      window.localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, audioInputDevices[0].deviceId);
      setSelectedMicId(audioInputDevices[0].deviceId);
    }
  }, [audioInputDevices]);

  const selectedLabel = useMemo(() => {
    const found = audioInputDevices?.find((d) => d.deviceId === selectedMicId);
    return found?.label || 'System Default';
  }, [audioInputDevices, selectedMicId]);

  const handleSelect = useCallback(
    async (deviceId: string) => {
      setSelectedMicId(deviceId);
      setAnchorEl(null);
      window.localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, deviceId);

      // Called once. It used to be invoked twice in a row, which fired two
      // overlapping restart() calls on the same track: restart stops the
      // MediaStreamTrack before re-acquiring, so the second getUserMedia
      // could hit a device the first had not released yet and reject,
      // leaving the track stopped with no replacement.
      replaceAudioTrack(deviceId);

      if (onAfterSelectDevice) await onAfterSelectDevice(deviceId);
    },
    [replaceAudioTrack, onAfterSelectDevice],
  );

  return (
    <div
      className={`fs-unmask ${className || ''}`}
      style={{
        height: layout === 'mini' ? 30 : 42,
        borderRadius: 999,
        background: '#E0E0E0',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Tooltip title={selectedLabel} placement="top">
        <IconButton
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="large"
          style={{
            width: layout === 'mini' ? 30 : 42,
            height: layout === 'mini' ? 30 : 42,
            borderRadius: 0,
            color: '#3D3D3D',
          }}
          data-cy-audio-device-dropdown
        >
          {menuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Tooltip>

      <button
        type="button"
        disabled={disabled}
        onClick={toggleAudioEnabled}
        data-cy-audio-toggle
        style={{
          width: layout === 'mini' ? 30 : 42,
          height: layout === 'mini' ? 30 : 42,
          border: 'none',
          background: 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'grid',
          placeItems: 'center',
          padding: 0,
        }}
      >
        <span
          style={{
            width: '-webkit-fill-available',
            height: '-webkit-fill-available',
            borderRadius: 999,
            background: '#F2F2F2',
            display: 'grid',
            placeItems: 'center',
            color: '#2C2C2C',
          }}
        >
          {isAudioEnabled ? <TbMicrophone size={20} /> : <TbMicrophoneOff size={20} />}
        </span>
      </button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ style: { width: 320, borderRadius: 12 } }}
        sx={{ zIndex: 2000 }}
      >
        <MenuItem disabled sx={{ opacity: 0.9 }}>
          <strong>Select a microphone</strong>
        </MenuItem>

        {!audioInputDevices || audioInputDevices.length === 0 ? (
          <MenuItem disabled>
            <span style={{ fontSize: 13, color: '#666' }}>No microphone devices found</span>
          </MenuItem>
        ) : (
          audioInputDevices.map((d, idx) => (
            <MenuItem
              key={d.deviceId}
              onClick={() => handleSelect(d.deviceId)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 1.25,
              }}
            >
              <span
                style={{
                  width: 24,
                  minWidth: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                }}
              >
                {d.deviceId === selectedMicId ? <CheckIcon fontSize="small" /> : null}
              </span>

              <span
                style={{
                  fontSize: 14,
                  lineHeight: 1.4,
                  display: 'block',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {d.label || `Microphone ${idx + 1}`}
              </span>
            </MenuItem>
          ))
        )}
      </Menu>
    </div>
  );
};

ToggleAudioButton.defaultProps = {
  disabled: false,
  className: '',
  onAfterSelectDevice: undefined,
};

export default ToggleAudioButton;
